import {
  Component,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  ViewChild,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { ImageInputComponent as ImageInputKtComponent } from 'src/app/_metronic/kt/components';
import { ImageInputEvent } from './image-input.types';
import { PublisherDataFormService } from 'src/app/publisher/settings/data-form/publisher-data-form.service';
import { LogService } from 'src/app/shared/log/log.service';
import { LogType } from 'src/app/shared/log/log.service.types';
@Component({
  selector: 'app-image-input',
  templateUrl: 'image-input.component.html',
  styleUrls: ['image-input.component.scss'],
})
export class ImageInputComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input()
  public allowedFileFormats: string = '.png, .jpg, .jpeg';

  @Input()
  public image: Blob | string | undefined;

  @Input()
  public wrapperClasses: string = 'image-input-outline';

  @Output()
  public update: EventEmitter<ImageInputEvent> = new EventEmitter<ImageInputEvent>();

  @ViewChild('root')
  private rootRef: ElementRef;

  public isImageLoaded: boolean = false;

  public logoImageComponent: ImageInputKtComponent;
  private shopId: string = '';
  private destroyed$: Subject<void> = new Subject();

  constructor(
    private readonly publisherDataFormService: PublisherDataFormService,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly logService: LogService
  ) {}

  public ngOnInit(): void {
    if (this.image) {
      this.isImageLoaded = true;
    }

    /**
     * @todo Handle the case where shopId might not be immediately available.
     * Consider deferring actions dependent on shopId until it's properly initialized
    */
    this.shopId = this.route.snapshot.params['contextId'] ||
    this.route.parent?.snapshot.params['contextId'];
  }

  /**
   * @todo Remove this code and extract out of this component.
   *      Since this component is generic and should contain any business logic
   */
  public deleteLogo(): void {
    if (!this.isImageLoaded) {
      return;
    }

    this.publisherDataFormService.deleteLogo(this.shopId).pipe(
      takeUntil(this.destroyed$),
    ).subscribe({
      next: () => {
        this.isImageLoaded = false;
      },
      error: err => {
        this.logService.captureException(
          new Error('Failed to delete logo'),
          {
            tags: {
              userType: LogType.PUBLISHER,
              component: 'ImageInputComponent',
            },
            extra: {
              shopId: this.shopId,
              error: err,
            },
          }
        );
      },
    });
  }

  public ngAfterViewInit(): void {
    this.logoImageComponent = ImageInputKtComponent.createInstance(this.rootRef.nativeElement) as ImageInputKtComponent;

    this.logoImageComponent.on('kt.imageinput.cancel', this.onCancelOrRemove);
    this.logoImageComponent.on('kt.imageinput.remove', this.onCancelOrRemove);

    this.logoImageComponent.on('kt.imageinput.change', (e: any) => {
      const reader = new FileReader();
      reader.readAsDataURL(e.target.files[0]);
      reader.onload = ({ target }) => {
        this.isImageLoaded = true;
        // For the situation when parent has OnPush strategy
        this.cdr.markForCheck();
        const image = new Image();
        image.src = target?.result as string;
        image.onload = () => {
          this.update.emit({
            file: e?.target?.files[0],
            params: {
              height: image.height,
              width: image.width,
              size: e.target.files[0].size,
              name: e.target.files[0]?.name
            },
          });
        };
      };
    });

    if (this.image) {
      this.updateValue(this.image);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (this.logoImageComponent && changes?.image?.currentValue) {
      this.updateValue(changes?.image?.currentValue);
    }
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onCancelOrRemove = () => {
    this.isImageLoaded = false;
    this.update.emit({
      file: null,
      params: {
        height: 0,
        width: 0,
        size: 0,
        name: '',
      },
    });
  };

  public reset(): void {
    this.logoImageComponent.setValue('');
    this.logoImageComponent.wrapperElement?.style.setProperty('background-image', 'none');
    this.isImageLoaded = false;
    this.cdr.markForCheck();
  }

  private updateValue(...file: Parameters<ImageInputKtComponent['setValue']>): void {
    this.isImageLoaded = true;
    this.logoImageComponent.setValue(...file);
  }
}
